// srcs/frontend/src/components/proposal/pages/RoiPage.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { useProposal } from '../state/ProposalContext.tsx';

import {
  useAiPromptsMutation,     // POST /ai/prompts
  useLazyGetDataQuery,      // GET /data
  useImportFileMutation,    // POST /files (export)
  useLazyGetFileQuery,      // GET /files (import)
} from '../../../services/solutionAPI.ts';

type ChatMsg = { role: 'User' | 'Assistant'; content: string; ts: number };

type RoiExport = {
  data_type: 'roi';
  session_id: string;
  seed_id: string;
  company_info: any;
  needs_info: any;
  seed_info: any;

  analysis_notes: string; // 任意条件（＝notes）
  chat: ChatMsg[];
  report_md: string | null;
  feedback_draft?: string;
  updated_at: number;
};

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_MS = 5 * 60 * 1000;
const AUTOSAVE_INTERVAL_MS = 30 * 1000;
const EXPORT_DEBOUNCE_MS = 500;

function isFinishedStatus(status: any) {
  const s = String(status ?? '').trim().toLowerCase();
  return s === 'finished' || s === '完了';
}
function isFailedStatus(status: any) {
  const s = String(status ?? '').trim().toLowerCase();
  return s === 'failed' || s === '失敗';
}

function fileName(sessionId: string, seedId: string) {
  return `roi_${sessionId}_${seedId}.json`;
}

function byteLength(str: string) {
  try {
    return new Blob([str]).size;
  } catch {
    return str.length;
  }
}

export default function RoiPage() {
  const {
    sessionId,
    selectedSeedId,
    companyInfo,
    needsInfo,
    selectedSeedInfo,
    setCompanyInfo,
    setNeedsInfo,
    setSelectedSeedInfo,
    setRoiDoneForKey, // ROI完了フラグ（sessionId×seedId）
  } = useProposal();

  const [postAiPrompt] = useAiPromptsMutation();
  const [triggerGetData] = useLazyGetDataQuery();
  const [postFiles] = useImportFileMutation();
  const [triggerGetFile] = useLazyGetFileQuery();

  // ---- ready を「sessionId & selectedSeedId のみ」に緩和 ----
  const readyBasics = Boolean(sessionId) && Boolean(selectedSeedId);
  const ready = readyBasics;

  // 左：メモ／会話／改善フィードバック
  const [notes, setNotes] = useState('');
  const [feedback, setFeedback] = useState('');
  const [chat, setChat] = useState<ChatMsg[]>([]);

  // 右：結果（Markdown）
  const [reportMd, setReportMd] = useState<string | null>(null);

  const [running, setRunning] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [lastFetchAt, setLastFetchAt] = useState<number | null>(null);

  const cancelRef = useRef({ cancelled: false });

  // stale closure回避
  const chatRef = useRef<ChatMsg[]>([]);
  const notesRef = useRef<string>('');
  const reportRef = useRef<string | null>(null);
  const feedbackRef = useRef<string>('');

  useEffect(() => { chatRef.current = chat; }, [chat]);
  useEffect(() => { notesRef.current = notes; }, [notes]);
  useEffect(() => { reportRef.current = reportMd; }, [reportMd]);
  useEffect(() => { feedbackRef.current = feedback; }, [feedback]);

  // 参照表示用
  const companyText = useMemo(() => JSON.stringify(companyInfo ?? {}, null, 2), [companyInfo]);
  const needsText = useMemo(() => JSON.stringify(needsInfo ?? {}, null, 2), [needsInfo]);
  const seedText = useMemo(() => JSON.stringify(selectedSeedInfo ?? {}, null, 2), [selectedSeedInfo]);

  // ----（任意）自動ハイドレーション：不足分を Seeds /data から補完 ----
  useEffect(() => {
    const needHydrate =
      readyBasics && (
        !companyInfo ||
        !needsInfo ||
        !selectedSeedInfo
      );

    if (!needHydrate || !sessionId) return;

    (async () => {
      try {
        // Seeds のデータを取得（seed_id は 'NULL'）
        const res: any = await triggerGetData({
          session_id: sessionId,
          data_type: 'seeds',
          seed_id: 'NULL',
        }).unwrap();

        if (!companyInfo && res?.company_info) setCompanyInfo(res.company_info);
        if (!needsInfo && res?.needs_info) setNeedsInfo(res.needs_info);

        // items から選択中 seed を補完
        const items = Array.isArray(res?.items) ? res.items : [];
        const found = items.find((it: any) => {
          const s = it?.seed_info ?? it?.seed ?? it;
          return s && String(s.seed_id ?? s.id ?? '') === String(selectedSeedId);
        });
        if (!selectedSeedInfo && found) {
          const s = found.seed_info ?? found.seed ?? found;
          const detail = s?.detailed_info ?? {};
          setSelectedSeedInfo({
            seed_id: String(s?.seed_id ?? s?.id ?? ''),
            seed_title: String(s?.title ?? s?.seed_title ?? ''),
            provider: String(s?.provider ?? ''),
            tech_domain: String(detail?.technical_field ?? s?.technical_field ?? ''),
            solvable_issues: Array.isArray(detail?.solvable_issues) ? detail.solvable_issues : [],
            case_studies: Array.isArray(detail?.case_studies) ? detail.case_studies : [],
            function_overview: String(detail?.function_overview ?? ''),
            differentiation_points: String(detail?.differentiation_points ?? ''),
            implementation_requirements: String(detail?.implementation_requirements ?? ''),
            cost_structure: String(detail?.cost_structure ?? ''),
            maturity_level: String(detail?.maturity_level ?? ''),
          });
        }
      } catch {
        // 補完は任意なので失敗しても無視
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyBasics, sessionId, selectedSeedId]);

  // ===== 保存ペイロード =====
  const buildPayload = (override?: Partial<RoiExport>): RoiExport | null => {
    if (!sessionId || !selectedSeedId) return null;
    return {
      data_type: 'roi',
      session_id: sessionId,
      seed_id: selectedSeedId,
      company_info: companyInfo ?? {},
      needs_info: needsInfo ?? {},
      seed_info: selectedSeedInfo ?? {},
      analysis_notes: notesRef.current,
      chat: chatRef.current,
      report_md: reportRef.current,
      feedback_draft: feedbackRef.current,
      updated_at: Date.now(),
      ...(override ?? {}),
    };
  };

  // ★ ai/prompts 用 structured payload builder（ROI）
  function buildAiPromptPayload(prompt: string, feedbackOverride?: string) {
    return {
      prompt,
      session_id: sessionId!,
      data_type: 'roi' as const,
      seed_id: selectedSeedId!,

      company_info: JSON.stringify(companyInfo ?? {}),
      needs_info: JSON.stringify(needsInfo ?? {}),
      seeds_info: JSON.stringify(selectedSeedInfo ?? {}),

      analysis_conditions: notesRef.current ?? '',

      ...(feedbackOverride
        ? { feedback: feedbackOverride }
        : {}),
    };
  }

  // ===== export（POST /files）=====
  const doExport = async (reason: string, payloadOverride?: RoiExport) => {
    if (!sessionId || !selectedSeedId) return;
    const payload = payloadOverride ?? buildPayload();
    if (!payload) return;

    const fname = fileName(sessionId, selectedSeedId);
    const json = JSON.stringify(payload);

    console.groupCollapsed(`[RoiPage] POST /files export (${reason})`);
    console.log('endpoint:', 'POST /files');
    console.log('request.file_name:', fname);
    console.log('request.file_context.bytes:', byteLength(json));
    console.log('request.file_context(json):', payload);
    console.groupEnd();

    try {
      const res = await postFiles({
        file_name: fname,
        file_context: json,
      }).unwrap();

      console.groupCollapsed(`[RoiPage] POST /files export response (${reason})`);
      console.log('response:', res);
      console.groupEnd();
    } catch (e) {
      console.groupCollapsed(`[RoiPage] POST /files export ERROR (${reason})`);
      console.log('error:', e);
      console.groupEnd();
    }
  };

  // ===== /files import（左ペイン & レポート & 履歴の完全復元） =====
  async function importFromFiles(reason: string) {
    if (!sessionId || !selectedSeedId) return;
    const fname = fileName(sessionId, selectedSeedId);

    console.groupCollapsed('[RoiPage] GET /files import', reason);
    console.log('file_name:', fname);
    console.groupEnd();

    try {
      const res: any = await triggerGetFile({ file_name: fname }).unwrap();
      const raw = res?.file_context ?? res?.items ?? res?.content ?? res;
      const payload: RoiExport = typeof raw === 'string' ? JSON.parse(raw) : raw;

      if (!payload || payload.data_type !== 'roi') return;

      // 左ペイン：会社/ニーズ/シーズを Context に
      setCompanyInfo(payload.company_info ?? {});
      setNeedsInfo(payload.needs_info ?? {});
      setSelectedSeedInfo(payload.seed_info ?? {});

      // 左ペイン：notes / feedback / chat
      setNotes(payload.analysis_notes ?? '');
      setFeedback(payload.feedback_draft ?? '');
      setChat(Array.isArray(payload.chat) ? payload.chat : []);

      // 右ペイン：レポート
      setReportMd(typeof payload.report_md === 'string' ? payload.report_md : null);

      console.groupCollapsed('[RoiPage] import finished (hydrate left pane & report)');
      console.log('chat restored:', Array.isArray(payload.chat) ? payload.chat.length : 0);
      console.groupEnd();
    } catch (e) {
      console.warn('[RoiPage] import failed (no saved file yet?)', e);
    }
  }

  // ===== chat/reportの自動保存（デバウンス）=====
  const exportTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (!readyBasics || !sessionId || !selectedSeedId) return;

    if (exportTimerRef.current) window.clearTimeout(exportTimerRef.current);
    exportTimerRef.current = window.setTimeout(() => {
      doExport('state_change(chat/report)');
      exportTimerRef.current = null;
    }, EXPORT_DEBOUNCE_MS);

    return () => {
      if (exportTimerRef.current) window.clearTimeout(exportTimerRef.current);
    };
  }, [
    readyBasics,
    sessionId,
    selectedSeedId,
    chat,
    reportMd,   // ← 右側成果物のみ
  ]);

  // ===== 保険の自動保存（30秒毎）=====
  useEffect(() => {
    if (!readyBasics) return;
    const id = window.setInterval(() => {
      doExport('autosave');
    }, AUTOSAVE_INTERVAL_MS);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyBasics, sessionId, selectedSeedId]);

  // ===== プロンプト =====
  function buildPromptInitial(): string {
    return `
あなたは幅広い業界に対応できるROI試算の専門コンサルタントです。以下を踏まえ、**まずは「既知/未知の整理と確認質問」**を提示し、続いて可能な範囲で**保守的前提による暫定試算**を提示してください。金額は**すべて「万円」**で表記します。

【入力情報】
- 企業情報:
${JSON.stringify(companyInfo ?? {}, null, 2)}
- ニーズ情報:
${JSON.stringify(needsInfo ?? {}, null, 2)}
- 選択シーズ情報:
${JSON.stringify(selectedSeedInfo ?? {}, null, 2)}
- 分析条件・特記事項（任意）:
${notesRef.current || '（特になし）'}

【金額表記ルール（厳守）】
- すべて **万円** 表記（円/千円は換算：例 500千円→50万円、120,000円→12万円）
- 数値は可能なら整数または一桁小数で簡潔に

【シーズ解釈（単一/複数）】
- 単一シーズ：当該シーズのコスト構造・効果をそのまま適用
- 複数シーズが関与する場合：**統合ソリューション**として扱い、**統合コスト/統合期間/統合リスク**も評価

【最初に出力すること（確認フェーズ）】
1) **既知情報の要約**（企業/ニーズ/シーズ/条件のポイント）
2) **不足情報の洗い出し**（数値/期間/確率/コスト内訳など）
3) **確認質問リスト（箇条書き）**
   - 重要度の高い順に、具体的な聞き方で
   - 回答があれば再計算精度が上がる旨を明記

【続いて出力すること（暫定試算フェーズ）】
以下の分析ステップで、**利用可能な情報のみ**を使って**保守的に**暫定値を提示。不足は**合理的な仮置き**を明記し、根拠（類似事例/一般相場/業界ベンチ）を併記すること。
1) 現状コストの定量化（内訳・根拠・合計）
2) 効果の試算（費用削減/収益向上/リスク軽減、各**実現確率**・**効果発現時期**）
3) 投資額の内訳（初期費用・年次運用費、**シーズ記載コストとの整合**）
4) ROI計算と回収計画（式を明示、**月次積み上げで回収月**を提示）
5) 現実性の検証（企業規模/業界ベンチ/条件整合・**リスク調整**の所見）

【出力構成（Markdown）】
### 1. 確認フェーズ
- 既知情報の要約
- 不足情報（リスト）
- 確認質問（重要度順・箇条書き）

### 2. 暫定ROI試算（保守的前提）
- 前提の整理（仮置きは明示）
- 現状コスト内訳と根拠
- 効果見積（確率/発現時期付き）
- 投資額の内訳（シーズコストとの整合）
- ROI計算と回収計画（**3年NPV**も簡易評価。割引率不明時は保守的に年**5〜8%**で仮置きし明記）
- リスクと感度分析（楽観/保守の **ROI%** と **回収月** 比較、主要リスクと軽減策）

### 3. 最終指標テーブル（暫定）
| 指標 | 楽観 | 保守 | 根拠/メモ |
|---|---:|---:|---|
| ROI(%) |  |  |  |
| 回収月 |  |  |  |
| 年間純利益（万円） |  |  |  |
| 初期費用（万円） |  |  |  |
| 年間運用費（万円） |  |  |  |

【重要】ユーザーから確認質問への回答が得られたら、再計算・表の更新・整合性検証を行う前提で記述してください。
`.trim();
  }

  function buildPromptImprove(userFeedback: string): string {
    const lastReport = reportRef.current ?? '（前回レポートなし）';
    const recentConv = chatRef.current.slice(-10).map((m) => `${m.role}: ${m.content}`).join('\n');

    return `
あなたはROI試算の専門コンサルタントです。**前回レポート**と**直近の会話**、**分析条件**、**ユーザーフィードバック**を踏まえ、数値と根拠の整合を再検証したうえでレポートを**改善・再計算**してください。金額は全て**万円**で統一します。

【前回レポート（Markdown）】
${lastReport}

【直近会話（最大10ターン）】
${recentConv || '（会話履歴なし）'}

【分析条件・特記事項】
${notesRef.current || '（特になし）'}

【ユーザーフィードバック】
${userFeedback}

【再検証ポイント】
- 数式・数値の検算（ROI式、回収月の月次積上、NPV）と**矛盾の解消**
- **シーズ記載コスト**および**企業規模/予算**との整合
- **楽観/保守**シナリオの妥当性（効果・確率・発現時期）
- リスク・感度分析の具体性強化（必要に応じて悲観の補足）

【出力要件】
- 改善後の**完全なROIレポート（Markdown）**（初回と同じ章立て）
- **最終指標テーブル**の更新
- **変更点一覧**（どこをどう変えたか）と**改善理由**（根拠を添えて）
`.trim();
  }

  // ===== /data から Markdown を抽出 =====
  function extractMarkdown(data: any): string {
    if (typeof data?.items === 'string') return data.items;
    if (typeof data?.report_md === 'string') return data.report_md;
    if (typeof data?.items?.report_md === 'string') return data.items.report_md;
    if (typeof data?.items?.md === 'string') return data.items.md;
    for (const k of Object.keys(data ?? {})) {
      if (k.startsWith('items') && typeof (data as any)[k] === 'string') {
        return String((data as any)[k]);
      }
    }
    return JSON.stringify(data?.items ?? data ?? {}, null, 2);
  }

  // ===== ポーリング =====
  async function pollUntilFinished(reason: string, baseChat: ChatMsg[]) {
    if (!sessionId || !selectedSeedId) return;
    setRunning(true);
    const start = Date.now();
    try {
      while (Date.now() - start < POLL_MAX_MS) {
        try {
          const q = { session_id: sessionId, data_type: 'roi' as const, seed_id: selectedSeedId };
          const res = await triggerGetData(q).unwrap();
          setLastFetchAt(Date.now());

          if (isFinishedStatus(res?.status)) {
            const md = extractMarkdown(res);
            const chatNext = [...baseChat, { role: 'Assistant', content: md, ts: Date.now() }];
            setChat(chatNext);
            setReportMd(md);

            // 完了フラグ
            setRoiDoneForKey?.(sessionId, selectedSeedId, true);

            // 自動保存
            await doExport(`poll_finished:${reason}`, buildPayload({
              chat: chatNext,
              report_md: md,
              updated_at: Date.now(),
            }) as RoiExport);
            return;
          }
          if (isFailedStatus(res?.status)) {
            const msg = `ROI failed: ${String(res?.status)}`;
            const chatNext = [...baseChat, { role: 'Assistant', content: msg, ts: Date.now() }];
            setChat(chatNext);
            setReportMd(msg);
            await doExport(`poll_failed:${reason}`, buildPayload({
              chat: chatNext,
              report_md: msg,
              updated_at: Date.now(),
            }) as RoiExport);
            return;
          }
        } catch {
          // 続行（一定間隔でリトライ）
        }
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      }
      setTimedOut(true);
    } finally {
      setRunning(false);
    }
  }

  // ===== 実行（初回）=====
  const onRun = async () => {
    if (!readyBasics || !sessionId || !selectedSeedId) return;

    const prompt = buildPromptInitial();

    const userMsg: ChatMsg = { role: 'User', content: 'ROI試算実行', ts: Date.now() };
    const baseChat = [...chatRef.current, userMsg];
    setChat(baseChat);
    setReportMd(null);
    setTimedOut(false);

    // ユーザー発話時点で保存
    await doExport('user_message_run', buildPayload({
      chat: baseChat,
      report_md: null,
      updated_at: Date.now(),
    }) as RoiExport);

    try {
      const body = buildAiPromptPayload(prompt);
      await postAiPrompt(body).unwrap();
    } catch (e) {
      const msg = `POST /ai/prompts に失敗: ${String((e as any)?.message ?? e)}`;
      const chatNext = [...baseChat, { role: 'Assistant', content: msg, ts: Date.now() }];
      setChat(chatNext);
      setReportMd(msg);
      await doExport('ai_prompts_failed', buildPayload({
        chat: chatNext,
        report_md: msg,
        updated_at: Date.now(),
      }) as RoiExport);
      return;
    }
    await pollUntilFinished('initial', baseChat);
  };

  // ===== 改善（2回目以降）=====
  const onImprove = async () => {
    if (!readyBasics || !sessionId || !selectedSeedId) return;
    const fb = feedback.trim();
    if (!fb) return;

    const prompt = buildPromptImprove(fb);
    const userMsg: ChatMsg = { role: 'User', content: fb, ts: Date.now() };
    setFeedback('');
    setTimedOut(false);

    const baseChat = [...chatRef.current, userMsg];
    setChat(baseChat);

    // 発話時点で保存
    await doExport('user_message_improve', buildPayload({
      chat: baseChat,
      updated_at: Date.now(),
    }) as RoiExport);

    try {
      const body = buildAiPromptPayload(prompt, fb);
      await postAiPrompt(body).unwrap();
    } catch (e) {
      const msg = `POST /ai/prompts（改善）に失敗: ${String((e as any)?.message ?? e)}`;
      const chatNext = [...baseChat, { role: 'Assistant', content: msg, ts: Date.now() }];
      setChat(chatNext);
      setReportMd(msg);
      await doExport('ai_prompts_failed_improve', buildPayload({
        chat: chatNext,
        report_md: msg,
        updated_at: Date.now(),
      }) as RoiExport);
      return;
    }

    await pollUntilFinished('improve', baseChat);
  };

  // ===== 再取得（/files → /data）=====
  const onRefetch = async () => {
    if (!readyBasics) return;

    // 1) まず /files から復元（左ペイン・レポート・履歴）
    await importFromFiles('refetch');

    // 2) /data をポーリングして最新化（完了時に自動保存も走る）
    await pollUntilFinished('refetch', chatRef.current);
  };

  // ===== 手動保存 =====
  const onSave = async () => {
    if (!sessionId || !selectedSeedId) return;
    await doExport('manual_save');
  };

  useEffect(() => () => { cancelRef.current.cancelled = true; }, []);

  return (
    <Box sx={{ height: 'calc(100vh - 32px)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, p: 2 }}>
      {/* 左 */}
      <Box sx={{ overflow: 'auto', pr: 1 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>ROI試算</Typography>
        {!readyBasics && <Alert severity="warning" sx={{ mb: 2 }}>Seedsでシーズを選択して session_id / seed_id を確定してください。</Alert>}

        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Button variant="contained" disabled={!readyBasics || running} onClick={onRun}>試算実行</Button>
            {running && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">取得中…（最大5分）</Typography>
              </Stack>
            )}
            {lastFetchAt && (
              <Typography variant="caption" color="text.secondary">最終取得: {new Date(lastFetchAt).toLocaleTimeString()}</Typography>
            )}
          </Stack>
          {timedOut && (
            <Alert severity="info" sx={{ mt: 1 }}>
              5分以内に finished になりませんでした。再取得してください。
            </Alert>
          )}
        </Paper>

        {/* 参照セクション（Accordionで折りたたみ表示） */}
        <Accordion disableGutters defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>企業情報（参照のみ）</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField value={companyText} multiline minRows={5} fullWidth size="small" InputProps={{ readOnly: true }} sx={{ mb: 2 }} />
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>ニーズ情報（参照のみ）</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField value={needsText} multiline minRows={8} fullWidth size="small" InputProps={{ readOnly: true }} sx={{ mb: 2 }} />
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>選択シーズ（参照のみ）</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField value={seedText} multiline minRows={8} fullWidth size="small" InputProps={{ readOnly: true }} sx={{ mb: 2 }} />
          </AccordionDetails>
        </Accordion>

        {/* 特記事項（任意） */}
        <Accordion disableGutters defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>特記事項（任意）</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              placeholder="例）段階導入 / 運用費を保守的に"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              minRows={4}
              fullWidth
              size="small"
              disabled={!readyBasics || running}
            />
          </AccordionDetails>
        </Accordion>

        {/* 改善（フィードバック） */}
        <Accordion disableGutters defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>改善（フィードバック）</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="例）初期費用をもう少し保守的に／回収月が現実的でない など"
              multiline
              minRows={3}
              fullWidth
              size="small"
              sx={{ mb: 1 }}
              disabled={!readyBasics || running}
            />
            <Button variant="contained" disabled={!readyBasics || running || !feedback.trim()} onClick={onImprove}>
              改善して再試算
            </Button>
          </AccordionDetails>
        </Accordion>

        {/* 分析履歴（対話） */}
        <Accordion disableGutters defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 700 }}>分析履歴（改善の過程）</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {chat.length === 0 ? (
              <Typography variant="body2" color="text.secondary">まだ履歴はありません。</Typography>
            ) : (
              <Stack spacing={1}>
                {chat.map((m, idx) => (
                  <Box key={idx} sx={{ p: 1, borderRadius: 1, bgcolor: m.role === 'User' ? '#e3f2fd' : '#f5f5f5' }}>
                    <Typography variant="caption" color="text.secondary">
                      {m.role} ・ {new Date(m.ts).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {m.content}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* 右：結果 */}
      <Box sx={{ overflow: 'auto', pl: 1 }}>
        {/* 右上 sticky：再取得 + 保存 */}
        <Box sx={{ position: 'sticky', top: 0, zIndex: 2, display: 'flex', justifyContent: 'flex-end', gap: 1, pb: 1, bgcolor: 'background.default' }}>
          <Button variant="outlined" size="small" disabled={!readyBasics || running} onClick={onRefetch}>再取得</Button>
          <Button variant="outlined" size="small" disabled={!readyBasics || running} onClick={onSave}>保存</Button>
        </Box>

        {!reportMd ? (
          <Box />
        ) : (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>ROI試算レポート</Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Box sx={{ bgcolor: '#fafafa', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{reportMd}</ReactMarkdown>
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
}